import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { StyledTable } from 'common/Table/StyledTable';
import AiCiTableWrapper, { CommunityName } from 'components/AiCompetitiveIntelligence/AiCiTableWrapper';
import DetailsButton from 'components/lib/DetailsButton';
import { renderBoldMarkup } from 'helpers/renderBoldMarkup';
import { CmiDigestWatchlistCommunityRow } from 'types/cmiDigestWatchlist';

const TableWrapper = styled(AiCiTableWrapper)`
  margin-top: 0;

  .ant-table-tbody > tr > td {
    padding: 20px 11px !important;
    vertical-align: top;
  }

  /* Ant Design injects a vertical divider between header cells via ::before - not wanted here. */
  .ant-table-thead > tr > th::before {
    display: none !important;
  }
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
  color: var(--text-primary);

  li {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-family: var(--font-bold);
  }
`;

const Metric = styled.div`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MetricLabel = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const MetricValue = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: var(--text-secondary);
`;

const SummaryTitle = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border: 1px solid var(--line-gray-strong);
  border-radius: 4px;
  background-color: var(--white);
  font-size: 11px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

type Props = {
  rows: CmiDigestWatchlistCommunityRow[];
  loading: boolean;
  runId: number;
};

const CommunitiesTable = ({ rows, loading, runId }: Props) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: 'COMMUNITY',
      key: 'community',
      render: (_: unknown, row: CmiDigestWatchlistCommunityRow) => (
        <div>
          <CommunityName>{row.community_name}</CommunityName>
          <div>
            <StatusBadge>{row.needs_attention ? 'Needs Attention' : 'Other'}</StatusBadge>
          </div>
        </div>
      ),
    },
    {
      title: 'NOTABLE ITEMS',
      key: 'notable_items',
      width: 260,
      render: (_: unknown, row: CmiDigestWatchlistCommunityRow) => (
        <div>
          {row.supporting_context.map((metric) => (
            <Metric key={metric.label}>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricValue>{metric.value}</MetricValue>
            </Metric>
          ))}
        </div>
      ),
    },
    {
      title: 'SUMMARY',
      key: 'summary',
      render: (_: unknown, row: CmiDigestWatchlistCommunityRow) => (
        <div>
          {row.notable_items.length > 0 && <SummaryTitle>{row.notable_items.join(' · ')}</SummaryTitle>}
          <BulletList>
            {row.summary.map((line, index) => (
              <li key={index}>{renderBoldMarkup(line)}</li>
            ))}
          </BulletList>
        </div>
      ),
    },
    {
      title: '',
      key: 'details',
      width: 110,
      render: (_: unknown, row: CmiDigestWatchlistCommunityRow) => (
        <DetailsButton onClick={() => navigate(`/communities-to-watch/${runId}/communities/${row.id}`)} />
      ),
    },
  ];

  return (
    <TableWrapper>
      <StyledTable
        columns={columns as any}
        dataSource={rows}
        rowKey={((row: CmiDigestWatchlistCommunityRow) => row.id) as any}
        loading={loading}
        pagination={false}
        size="middle"
        bordered={false}
      />
    </TableWrapper>
  );
};

export default CommunitiesTable;
